import { getWidgetDimensions } from '../constants/constants'
import { WidgetConfig, WidgetRow } from '../types'

/**
 * Группирует виджеты в строки на основе их визуального расположения
 * @param widgets - массив виджетов
 * @param columnsCount - количество колонок в сетке
 * @returns массив строк с виджетами
 */
export const groupWidgetsIntoRows = (
	widgets: WidgetConfig[],
	columnsCount: number,
): WidgetRow[] => {
	if (widgets.length === 0) return []

	const rows: WidgetRow[] = []
	let currentRowIndex = 0
	let currentRowWidth = 0
	let currentRow: WidgetConfig[] = []
	const timestamp = Date.now()

	for (const widget of widgets) {
		const dimensions = getWidgetDimensions(widget.size)
		const isFullWidget = dimensions.w === -1
		const widgetWidth = isFullWidget ? columnsCount : dimensions.w

		// FULL виджеты всегда должны быть единственными в строке
		if (isFullWidget) {
			// Если текущая строка не пустая, сохраняем её
			if (currentRow.length > 0) {
				rows.push({
					id: `row-${timestamp}-${currentRowIndex}`,
					widgets: [...currentRow],
					rowIndex: currentRowIndex,
				})
				currentRowIndex++
				currentRowWidth = 0
				currentRow = []
			}
			// Создаем новую строку только с FULL виджетом
			rows.push({
				id: `row-${timestamp}-${currentRowIndex}`,
				widgets: [widget],
				rowIndex: currentRowIndex,
			})
			currentRowIndex++
			currentRowWidth = 0
			currentRow = []
			continue
		}

		// Для не-FULL виджетов: если виджет не помещается в текущую строку, создаем новую
		if (currentRow.length > 0 && currentRowWidth + widgetWidth > columnsCount) {
			// Сохраняем текущую строку
			rows.push({
				id: `row-${timestamp}-${currentRowIndex}`,
				widgets: [...currentRow],
				rowIndex: currentRowIndex,
			})

			// Начинаем новую строку
			currentRowIndex++
			currentRowWidth = 0
			currentRow = []
		}

		// Добавляем виджет в текущую строку
		currentRow.push(widget)
		currentRowWidth += widgetWidth

		// Если строка заполнена, завершаем её
		if (currentRowWidth >= columnsCount) {
			rows.push({
				id: `row-${timestamp}-${currentRowIndex}`,
				widgets: [...currentRow],
				rowIndex: currentRowIndex,
			})

			currentRowIndex++
			currentRowWidth = 0
			currentRow = []
		}
	}

	// Добавляем последнюю строку, если она не пустая
	if (currentRow.length > 0) {
		rows.push({
			id: `row-${timestamp}-${currentRowIndex}`,
			widgets: currentRow,
			rowIndex: currentRowIndex,
		})
	}

	return rows
}

/**
 * Преобразует строки обратно в плоский массив виджетов
 * @param rows - массив строк
 * @returns массив виджетов
 */
export const flattenRowsToWidgets = (rows: WidgetRow[]): WidgetConfig[] => {
	return rows.flatMap(row => row.widgets)
}

/**
 * Находит виджет в строках и возвращает информацию о его расположении
 * @param widgetId - ID виджета
 * @param rows - массив строк
 * @returns объект с виджетом и ID строки, или null если не найден
 */
export const findWidgetInRows = (
	widgetId: string,
	rows: WidgetRow[],
): { widget: WidgetConfig; rowId: string } | null => {
	for (const row of rows) {
		const widget = row.widgets.find(w => w.id === widgetId)
		if (widget) {
			return { widget, rowId: row.id }
		}
	}
	return null
}

/**
 * Перемещает виджет между строками
 * @param rows - массив строк
 * @param fromRowId - ID исходной строки
 * @param toRowId - ID целевой строки
 * @param widgetId - ID виджета для перемещения
 * @returns новый массив строк с перемещенным виджетом (без пустых строк)
 */
export const moveWidgetBetweenRows = (
	rows: WidgetRow[],
	fromRowId: string,
	toRowId: string,
	widgetId: string,
): WidgetRow[] => {
	// Находим виджет для перемещения
	const fromRow = rows.find(r => r.id === fromRowId)
	const widget = fromRow?.widgets.find(w => w.id === widgetId)
	if (!widget) return rows

	const widgetDimensions = getWidgetDimensions(widget.size)
	const isFullWidget = widgetDimensions.w === -1

	// Проверяем допустимость перемещения ДО изменений (атомарность)
	const targetRow = rows.find(r => r.id === toRowId)
	if (targetRow) {
		if (isFullWidget && targetRow.widgets.length > 0) return rows
		const targetHasFullWidget = targetRow.widgets.some(
			w => getWidgetDimensions(w.size).w === -1,
		)
		if (targetHasFullWidget && !isFullWidget) return rows
	}

	const updatedRows = rows.map(row => {
		if (row.id === fromRowId) {
			return {
				...row,
				widgets: row.widgets.filter(w => w.id !== widgetId),
			}
		}
		if (row.id === toRowId) {
			return {
				...row,
				widgets: [...row.widgets, widget],
			}
		}
		return row
	})

	// Удаляем пустые строки и обновляем индексы
	return removeEmptyRows(updatedRows)
}

/**
 * Переупорядочивает виджеты внутри строки
 * @param rows - массив строк
 * @param rowId - ID строки
 * @param newWidgetsOrder - новый порядок виджетов
 * @returns новый массив строк с обновленной строкой
 */
export const reorderWidgetsInRow = (
	rows: WidgetRow[],
	rowId: string,
	newWidgetsOrder: WidgetConfig[],
): WidgetRow[] => {
	return rows.map(row => {
		if (row.id === rowId) {
			return {
				...row,
				widgets: newWidgetsOrder,
			}
		}
		return row
	})
}

/**
 * Добавляет виджет в строку
 * @param rows - массив строк
 * @param rowId - ID строки
 * @param widget - виджет для добавления
 * @returns новый массив строк с добавленным виджетом
 */
export const addWidgetToRow = (
	rows: WidgetRow[],
	rowId: string,
	widget: WidgetConfig,
): WidgetRow[] => {
	const widgetDimensions = getWidgetDimensions(widget.size)
	const isFullWidget = widgetDimensions.w === -1

	return rows.map(row => {
		if (row.id === rowId) {
			// Проверяем, нет ли уже такого виджета
			if (row.widgets.some(w => w.id === widget.id)) {
				return row
			}

			// FULL виджеты могут быть только в пустых строках
			if (isFullWidget && row.widgets.length > 0) {
				// Не добавляем FULL виджет в строку с другими виджетами
				return row
			}

			// Если в строке уже есть FULL виджет, не добавляем другие виджеты
			const hasFullWidget = row.widgets.some(
				w => getWidgetDimensions(w.size).w === -1,
			)
			if (hasFullWidget && !isFullWidget) {
				return row
			}

			return {
				...row,
				widgets: [...row.widgets, widget],
			}
		}
		return row
	})
}

/**
 * Создает новую пустую строку
 * @param rowIndex - индекс новой строки
 * @returns новая строка
 */
export const createEmptyRow = (rowIndex: number): WidgetRow => {
	return {
		id: `row-${Date.now()}-${rowIndex}`,
		widgets: [],
		rowIndex,
	}
}

/**
 * Удаляет пустые строки и обновляет индексы
 * @param rows - массив строк
 * @returns новый массив строк без пустых строк
 */
export const removeEmptyRows = (rows: WidgetRow[]): WidgetRow[] => {
	const filteredRows = rows.filter(row => row.widgets.length > 0)
	// Обновляем индексы строк
	return filteredRows.map((row, index) => ({
		...row,
		rowIndex: index,
	}))
}

/**
 * Вставляет новую строку с виджетом в указанную позицию
 * @param rows - массив строк
 * @param insertAfterRowId - ID строки, после которой нужно вставить (null для вставки в начало)
 * @param widget - виджет для добавления в новую строку
 * @returns новый массив строк с вставленной строкой
 */
export const insertRowWithWidget = (
	rows: WidgetRow[],
	insertAfterRowId: string | null,
	widget: WidgetConfig,
): WidgetRow[] => {
	const timestamp = Date.now()
	let insertIndex = 0

	if (insertAfterRowId) {
		const afterRowIndex = rows.findIndex(r => r.id === insertAfterRowId)
		if (afterRowIndex !== -1) {
			insertIndex = afterRowIndex + 1
		} else {
			insertIndex = rows.length
		}
	}

	const newRow: WidgetRow = {
		id: `row-${timestamp}-${insertIndex}`,
		widgets: [widget],
		rowIndex: insertIndex,
	}

	// Вставляем новую строку и обновляем индексы последующих строк
	const newRows = [...rows]
	newRows.splice(insertIndex, 0, newRow)

	// Обновляем индексы строк после вставленной
	return newRows.map((row, index) => ({
		...row,
		rowIndex: index,
	}))
}
